import { useCallback, useMemo, useState } from 'react';
import { useLazyLoadQuery } from 'react-relay';
import { useParams } from '@tanstack/react-router';
import { BlockStack, Box, Card, Link, Typography } from '@pv-frontend/ui-components-web';
import { vaultDetailQuery } from '../relay/vault-detail-query';
import type { vaultDetailQuery as VaultDetailQuery } from '../relay/__generated__/vaultDetailQuery.graphql';
import { useVaultToggles } from '../relay/use-vault-toggles';
import { useProGrantActions } from '../relay/use-pro-grant-actions';
import { useHomeFeed } from '../relay/use-home-feed';
import { HomeFeedPanel } from './home-feed-panel';
import { VaultDetail, type VaultDetailView } from './vault-detail';
import type { GrantActionView } from './complimentary-standing';

/**
 * Cross-tenant vault detail for one vault (slice from PRD #11), reached from the
 * Tenants vault list. Suspends while the query loads; renders the detail plus
 * the Complimentary Pro grant/revoke and ring / override write controls, or a
 * not-found state when no vault has the id. The read is recorded server-side
 * against the operator.
 *
 * `fetchKey` bumps after a confirmed grant or revoke to refetch the detail (the
 * mutation payloads don't carry the grant facts). The first load keeps
 * `store-or-network`; a refetch reads through the store rather than re-suspending.
 */
export function VaultDetailScreen() {
    const { vaultId } = useParams({ from: '/vaults/$vaultId' });
    const [fetchKey, setFetchKey] = useState(0);
    const data = useLazyLoadQuery<VaultDetailQuery>(
        vaultDetailQuery,
        { vaultId },
        { fetchPolicy: fetchKey === 0 ? 'store-or-network' : 'store-and-network', fetchKey },
    );
    const refresh = useCallback(() => setFetchKey((key) => key + 1), []);

    return (
        <Box padding="600" maxWidth={960}>
            <BlockStack gap="500">
                <Link href="/tenants">← Back to tenants</Link>
                {data.vaultDetail ? (
                    <VaultDetailContent
                        base={mapVault(data.vaultDetail)}
                        featureKeys={data.featureFlags.map((flag) => flag.key)}
                        onRefresh={refresh}
                    />
                ) : (
                    <Card>
                        <Typography tone="text-secondary">
                            No vault was found for this id. It may have been removed.
                        </Typography>
                    </Card>
                )}
            </BlockStack>
        </Box>
    );
}

/** Maps the Relay `vaultDetail` payload into the presentational view model. */
function mapVault(vault: NonNullable<VaultDetailQuery['response']['vaultDetail']>): VaultDetailView {
    return {
        vaultId: vault.vaultId,
        name: vault.name,
        identifier: vault.identifier,
        jurisdiction: vault.jurisdiction,
        ring: vault.ring,
        createdUtc: vault.createdUtc,
        planName: vault.planName ?? null,
        subscriptionStatus: vault.subscriptionStatus ?? null,
        planBucket: vault.planBucket,
        canGrant: vault.canGrant,
        complimentary: {
            isComplimentary: vault.complimentary.isComplimentary,
            complimentaryUntil: vault.complimentary.complimentaryUntil ?? null,
            lastGrant: mapGrantAction(vault.complimentary.lastGrant),
            lastRevoke: mapGrantAction(vault.complimentary.lastRevoke),
            revokedEarly: vault.complimentary.revokedEarly,
        },
        members: vault.members.map((member) => ({
            accountId: member.accountId,
            email: member.email ?? null,
            roleName: member.roleName,
            joinedUtc: member.joinedUtc,
            lastLoginUtc: member.lastLoginUtc ?? null,
        })),
        overrides: vault.overrides.map((override) => ({
            flagKey: override.flagKey,
            state: override.state,
        })),
    };
}

type GrantActionPayload = NonNullable<
    VaultDetailQuery['response']['vaultDetail']
>['complimentary']['lastGrant'];

function mapGrantAction(action: GrantActionPayload): GrantActionView | null {
    return action
        ? {
              operator: action.operator,
              term: action.term ?? null,
              reason: action.reason,
              occurredUtc: action.occurredUtc,
          }
        : null;
}

/**
 * Binds the vault view model to the optimistic toggle hook and the presentational
 * component. Split out so the hook is only ever called with a real (non-null)
 * vault, keeping the rules of hooks satisfied on the not-found path.
 */
function VaultDetailContent({
    base,
    featureKeys,
    onRefresh,
}: {
    base: VaultDetailView;
    featureKeys: readonly string[];
    onRefresh: () => void;
}) {
    const { vault, busyKeys, onSetRing, onSetOverride, onClearOverride } = useVaultToggles(base);
    const { onGrantPro, onRevokePro } = useProGrantActions(base.vaultId, onRefresh);

    // The household's flag state follows the ring and the HOME_FEED override; refetch once either settles.
    const flagInFlight = busyKeys.has('ring') || busyKeys.has('override:HOME_FEED');
    const homeFeedKey = flagInFlight
        ? null
        : `${vault.ring}|${vault.overrides.find((o) => o.flagKey === 'HOME_FEED')?.state ?? ''}`;
    const { household, inspect, reloadHousehold, reloadFailed } = useHomeFeed(base.vaultId, homeFeedKey);
    const memberEmails = useMemo(
        () => new Map(base.members.flatMap((m) => (m.email ? [[m.accountId, m.email] as const] : []))),
        [base.members],
    );

    return (
        <BlockStack gap="500">
            <VaultDetail
                vault={vault}
                featureKeys={featureKeys}
                busyKeys={busyKeys}
                onSetRing={onSetRing}
                onSetOverride={onSetOverride}
                onClearOverride={onClearOverride}
                onGrantPro={onGrantPro}
                onRevokePro={onRevokePro}
            />
            <HomeFeedPanel
                household={household}
                inspect={inspect}
                onReloadHousehold={reloadHousehold}
                reloadFailed={reloadFailed}
                memberEmails={memberEmails}
            />
        </BlockStack>
    );
}
